// Real-auth context coordinator. Sample data belongs only to explicit demo mode.
// Requests collect off-global; only the current generation can publish a snapshot.
export function createLiveHydrator(api, maps, win = window) {
  let generation = 0;
  let mutations = Promise.resolve();
  function mutate(fn) {
    const pending = mutations.then(fn, fn);
    mutations = pending.catch(() => {});
    return pending;
  }
  const M = () => win.MAV;
  const viewer = () => win.RBAC?.viewer();
  const key = (user, context) => user && context ? `${user}:${context}` : null;
  const emit = () => win.dispatchEvent(new CustomEvent('goose-rbac-change'));
  const current = ticket => ticket.generation === generation;
  function clearData() {
    const m = M();
    for (const name of ['jobs', 'clients', 'team', 'pods', 'issues', 'kbRequests', 'stageScores', 'reviewFindings', 'liveLog']) m[name] = [];
    m.kbProfiles = {}; m.summary = null; m.metrics = null; m.costByStage = null; m.passRate = null;
    m.credits = null; m.__lastStartedJob = null; delete win.__lastStartedJob;
    m.__live = true; m.__liveClients = false; m.__liveTeam = false; m.__liveIssues = false; m.__liveKbRequests = false;
    m.clientBySlug = slug => m.clients.find(c => c.slug === slug) || maps.synthClient(slug);
    m.memberById = id => m.team.find(member => member.id === id) || null;
    m.teamById = id => m.pods.find(pod => pod.id === id) || null;
    m.issueById = id => m.issues.find(issue => issue.id === id) || null;
    m.resetCodexUsage?.();
  }
  function begin({ identity = false, preserveStubs = false, background = false } = {}) {
    const m = M();
    const ticket = { generation: ++generation, background, previousKey: m.__contextKey, previousAuthority: m.__authorityFingerprint,
      stubs: preserveStubs ? (m.jobs || []).filter(j => j._stub && j._contextKey && j._contextKey === m.__contextKey) : [] };
    if (!background) { m.__contextEpoch = generation; clearData(); }
    if (identity) {
      m.currentUser = null; m.memberships = []; m.orgTier = null; m.activeWorkspaceId = null;
      m.me = { assignments: [] }; m.viewer = null; m.__contextKey = null; m.__authorityFingerprint = null;
      win.RBAC?.setViewer(null);
    }
    if (!background) m.contextLoad = { status: 'loading', errors: {} };
    emit();
    return ticket;
  }
  function finish(ticket, status, errors = {}) {
    if (!current(ticket)) return { ok: false, reason: 'superseded' };
    M().contextLoad = { status, errors }; emit();
    return { ok: status === 'ready', reason: status === 'ready' ? undefined : status, errors, count: M().jobs.length };
  }
  function reset() { const ticket = begin({ identity: true }); finish(ticket, 'signed_out'); }
  const capture = async fn => {
    try { return { value: await fn() }; }
    catch (e) { return { error: e.kind || 'error', status: e.status }; }
  };
  function identityOf(me) {
    if (!me?.user?.id || !me.activeWorkspaceId) throw new Error('identity_unavailable');
    return key(me.user.id, me.activeWorkspaceId);
  }
  function authorityOf(me) {
    return JSON.stringify({ id: me.user.id, role: me.user.role, orgTier: me.orgTier || null,
      memberships: [...(me.memberships || [])].sort((a, b) => a.workspaceId.localeCompare(b.workspaceId)),
      assignments: [...(me.assignments || [])].map(a => JSON.stringify(a)).sort() });
  }
  async function load(ticket, requestedContext) {
    try {
      await mutations;
      if (!current(ticket)) return { ok: false, reason: 'superseded' };
      const me = await api.me(), identity = identityOf(me);
      if (!current(ticket)) return { ok: false, reason: 'superseded' };
      if (ticket.background && ticket.previousAuthority !== authorityOf(me)) {
        // Retained data belongs to the previous authority snapshot, not merely
        // the same user/workspace. Revocation beats draft preservation.
        ticket.background = false; ticket.stubs = [];
        clearData(); M().__contextEpoch = generation; M().__contextKey = null;
        M().currentUser = null; M().me = { assignments: [] }; win.RBAC.setViewer(null);
        M().contextLoad = { status: 'loading', errors: {} }; emit();
      }
      const v = win.RBAC.viewerFromMe(me);
      if ((!ticket.previousKey || ticket.previousKey.startsWith(me.user.id + ':')) && (requestedContext === 'org' || requestedContext === 'all')) v.ctx = requestedContext;
      const plan = win.RBAC.buildsPlan(v);
      const contextKey = key(me.user.id, v.ctx);
      const calls = plan.source === 'single' ? {
        builds: () => api.builds(v.ctx), summary: () => api.summary(), clients: () => api.clients(),
        team: () => api.team(), costByStage: () => api.costByStage(), metrics: () => api.metrics(),
        issues: () => api.issues({ pageSize: 200 }), ratings: () => api.ratingsSummary(), kbProposals: () => api.kbProposals(),
      } : plan.source === 'org' ? { builds: () => api.orgBuilds() } : plan.source === 'fanout' ? {
        // Scoped membership-gated reads: never mutate the session during fan-out.
        builds: async () => {
          const parts = await Promise.all(plan.workspaces.map(async workspaceId => {
            const result = await api.builds(workspaceId);
            if (!Array.isArray(result?.builds)) throw new Error('invalid_builds');
            const member = v.memberships.find(m => m.workspaceId === workspaceId);
            return result.builds.map(b => ({ ...b, _wsSlug: member?.slug, _wsName: member?.name }));
          }));
          return { builds: parts.flat() };
        },
      } : { builds: () => Promise.reject(new Error('no_context')) };
      const entries = await Promise.all(Object.entries(calls).map(async ([name, fn]) => [name, await capture(fn)]));
      if (!current(ticket)) return { ok: false, reason: 'superseded' };
      // Detect a session changed elsewhere while implicit tenant endpoints ran.
      const confirmed = await api.me();
      if (!current(ticket)) return { ok: false, reason: 'superseded' };
      if (identityOf(confirmed) !== identity || authorityOf(confirmed) !== authorityOf(me)) {
        clearData(); M().__contextEpoch = generation; M().__contextKey = null;
        return finish(ticket, 'error', { identity: 'context_changed' });
      }
      const result = Object.fromEntries(entries), errors = {};
      for (const [name, r] of entries) if (r.error) errors[name] = r.error;
      if (Object.values(errors).includes('auth')) {
        reset(); return { ok: false, reason: 'auth' };
      }
      if (result.builds?.status === 403) {
        reset(); return { ok: false, reason: 'forbidden' };
      }
      if (ticket.background && contextKey === ticket.previousKey && (errors.builds || !Array.isArray(result.builds?.value?.builds))) {
        // A failed same-context refresh is stale, not a context transition. Keep
        // mounted editor drafts; never label retained data as freshly loaded.
        errors.builds ||= 'invalid_response';
        finish(ticket, 'ready', { ...errors, refresh: 'stale_previous_snapshot' });
        return { ok: false, reason: 'refresh_failed', errors };
      }
      const take = (name, field) => {
        const value = field ? result[name]?.value?.[field] : result[name]?.value;
        if (value == null && result[name] && !errors[name]) errors[name] = 'invalid_response';
        return value;
      };
      const array = (name, field) => {
        const value = take(name, field);
        if (!Array.isArray(value)) { if (result[name]) errors[name] ||= 'invalid_response'; return []; }
        return value;
      };
      const m = M();
      if (contextKey !== ticket.previousKey) { m.__contextEpoch = generation; clearData(); }
      m.currentUser = { ...me.user, workspaceId: me.activeWorkspaceId, initials: maps.initialsFrom(me.user.name) };
      m.memberships = me.memberships || []; m.orgTier = me.orgTier || null; m.activeWorkspaceId = me.activeWorkspaceId;
      m.me = { assignments: me.assignments || [] }; m.__contextKey = contextKey; m.viewerContext = v.ctx;
      m.__authorityFingerprint = authorityOf(me);
      if (plan.source === 'single') {
        const clients = array('clients', 'clients');
        m.clients = clients.map(maps.mapClient);
        m.kbProfiles = Object.fromEntries(clients.map(c => [c.slug, maps.mapKbProfileStub(c)]));
        m.__liveClients = !errors.clients;
        const pods = array('team', 'pods'), team = array('team', 'team');
        const byUuid = Object.fromEntries(pods.map(p => [p.id, p]));
        m.pods = pods.map(maps.mapTeamPod); m.team = team.map(u => maps.mapTeamMember(u, byUuid));
        const members = Object.fromEntries(m.team.map(u => [u._id, u.id]));
        m.pods.forEach(p => { if (p._leadUuid) p.leadId = members[p._leadUuid] || null; });
        m.__liveTeam = !errors.team;
        m.summary = take('summary', 'summary') || null; m.metrics = take('metrics') || null;
        const costs = array('costByStage', 'costByStage');
        m.costByStage = errors.costByStage ? null : { rows: costs, totalCost: Number(result.costByStage.value.totalCost) || 0 };
        m.issues = array('issues', 'rows').map(maps.mapIssue); m.__liveIssues = !errors.issues;
        m.kbRequests = array('kbProposals', 'proposals'); m.__liveKbRequests = !errors.kbProposals;
        const ratings = take('ratings');
        for (const subject of ratings?.bySubject || []) {
          const member = m.team.find(x => x.id === subject.ext_id);
          if (member) for (const field of ['rating', 'comment', 'timeSaved', 'wouldShip', 'lastBuild']) {
            if (subject[field] != null) member[field] = subject[field];
          }
        }
        const names = { ingest: 'Ingestion', semantic: 'Semantic', plan: 'Planning', exec: 'Execution', qa: 'QA', approve: 'Approval' };
        m.stageScores = (ratings?.stageScores || []).map(s => ({ ...s, name: names[s.id] || s.id }));
        m.passRate = ratings?.passRate ?? null;
      }
      const rows = array('builds', 'builds');
      const jobs = rows.map(plan.source === 'org' ? maps.mapOrgRow : b => ({ ...maps.mapBuild(b), ...('_wsSlug' in b ? { _wsSlug: b._wsSlug, _wsName: b._wsName } : {}) }));
      const ids = new Set(jobs.map(j => j.id));
      m.jobs = [...(!errors.builds && contextKey === ticket.previousKey ? ticket.stubs.filter(j => !ids.has(j.id)) : []), ...jobs];
      win.RBAC.setViewer(v);
      const state = finish(ticket, errors.builds ? 'error' : 'ready', errors);
      if (state.ok) m.refreshCodexUsage?.();
      return state;
    } catch (e) {
      if (!current(ticket)) return { ok: false, reason: 'superseded' };
      if (e.kind === 'auth' || e.status === 403) { reset(); return { ok: false, reason: e.status === 403 ? 'forbidden' : 'auth' }; }
      if (ticket.background && M().__contextKey === ticket.previousKey) {
        finish(ticket, 'ready', { refresh: 'stale_previous_snapshot' });
        return { ok: false, reason: 'refresh_failed' };
      }
      clearData();
      return finish(ticket, 'error', { load: e.kind || 'unavailable' });
    }
  }
  function hydrate() {
    const ctx = viewer()?.ctx;
    return load(begin({ preserveStubs: true, background: !!M().__contextKey && M().contextLoad?.status === 'ready' }), ctx);
  }
  async function authenticate(email, password) {
    const ticket = begin({ identity: true });
    try {
      if (email !== undefined) await mutate(() => api.login(email, password));
      if (!current(ticket)) return { ok: false, reason: 'superseded' };
      return await load(ticket);
    } catch (e) {
      if (current(ticket)) finish(ticket, 'error', { login: e.kind || 'unavailable' });
      throw e;
    }
  }
  async function switchContext(context) {
    const ticket = begin();
    try {
      if (context !== 'org' && context !== 'all') await mutate(() => api.switchWorkspace(context));
      if (!current(ticket)) return { ok: false, reason: 'superseded' };
      return await load(ticket, context);
    } catch (e) {
      return finish(ticket, 'error', { switch: e.kind || 'unavailable' });
    }
  }
  async function signOut(all = false) {
    reset();
    // Do not claim server logout on a failed request. The caller displays error.
    const ticket = { generation };
    try {
      await mutate(() => all ? api.logoutAll() : api.logout());
      return { ok: current(ticket) };
    }
    catch (e) { finish(ticket, 'error', { logout: 'server_session_may_still_be_active' }); throw e; }
  }
  async function loadKbProfile(slug) {
    const ticket = { generation }, contextKey = M().__contextKey;
    if (!slug || !contextKey || ['org', 'all'].includes(M().viewerContext)) return null;
    const old = M().kbProfiles[slug];
    if (old && !old._stub && old.approvedMarkdown) return old;
    try {
      const r = await api.client(slug);
      if (!current(ticket) || M().__contextKey !== contextKey) return null;
      if (!r?.client?.approved_markdown) return old || null;
      const full = { ...maps.mapKbProfileStub(r.client), approvedMarkdown: r.client.approved_markdown };
      delete full._stub; M().kbProfiles[slug] = full; return full;
    } catch (_) { return current(ticket) && M().__contextKey === contextKey ? old || null : null; }
  }
  return { hydrate, authenticate, switchContext, signOut, reset, loadKbProfile };
}
